import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { paginaValida } from '../lecturas/paginas';

/**
 * El campo para saltar a una página.
 *
 * **Salta al confirmar, no en cada tecla.** Antes era un campo numérico que
 * navegaba con cada cambio: para ir a la 100 se escribía «1» y ya estaba en la
 * 1, luego «0» y estaba en la 10, y la 100 llegaba de tercera. Ahora se escribe
 * el número completo y se va con Enter o al salir del campo; Escape lo deja
 * como estaba. Un número que no es una página no mueve nada: el campo se marca y
 * vuelve a la página actual.
 */
export function SelectorDePagina({
  pagina,
  total,
  deshabilitado,
  onIr,
}: {
  pagina: number;
  total: number;
  deshabilitado: boolean;
  onIr: (pagina: number) => void;
}) {
  const { t } = useTranslation();
  const [borrador, setBorrador] = useState(String(pagina));
  const [editando, setEditando] = useState(false);
  const [invalido, setInvalido] = useState(false);
  // Enter confirma y luego quita el foco, y quitar el foco también confirma: sin
  // esta marca se saltaba dos veces a la misma página.
  const pendiente = useRef(false);

  // Mientras no se está escribiendo, el campo sigue a la página de verdad
  // —la que cambia con las flechas o al desplazarse—.
  useEffect(() => {
    if (!editando) setBorrador(String(pagina));
  }, [pagina, editando]);

  useEffect(() => {
    if (!invalido) return;
    const temporizador = window.setTimeout(() => setInvalido(false), 1_800);
    return () => window.clearTimeout(temporizador);
  }, [invalido]);

  const confirmar = () => {
    if (!pendiente.current) return;
    pendiente.current = false;
    setEditando(false);
    const destino = paginaValida(borrador, total);
    if (destino === null) {
      if (borrador.trim() !== String(pagina)) setInvalido(true);
      setBorrador(String(pagina));
      return;
    }
    if (destino !== pagina) onIr(destino);
  };

  return (
    <label className="visor__conteo mono">
      <span className="visualmente-oculto">{t('viewer.goToPage')}</span>
      <input
        className={`visor__pagina-campo mono${invalido ? ' visor__pagina-campo--invalido' : ''}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-invalid={invalido || undefined}
        title={invalido ? t('viewer.pageOutOfRange', { total }) : t('viewer.goToPageHelp')}
        value={borrador}
        disabled={deshabilitado}
        size={Math.max(2, String(total).length)}
        onFocus={(e) => {
          pendiente.current = true;
          setEditando(true);
          e.currentTarget.select();
        }}
        onChange={(e) => setBorrador(e.target.value.replace(/\D/g, '').slice(0, String(total).length + 1))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmar();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            pendiente.current = false;
            setBorrador(String(pagina));
            setEditando(false);
            e.currentTarget.blur();
          }
        }}
        onBlur={confirmar}
      />
      <span aria-hidden="true">{t('viewer.ofTotal', { total })}</span>
    </label>
  );
}
